import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, model, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatOption, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';

import { AgreementForm, AgreementFormData } from '@lib/agreement-form';
import { PoSelect } from '@lib/po-select';
import { ReservationData, ReservationDialog } from '@lib/reservation-dialog';

import { Agreement, AgreementStatuses } from '../schemas';
import { Jira } from '../service';
import { AgreementService } from '../service/agreement';
import { Auth } from '../service/auth';
import { ClientService } from '../service/client';
import { ProductService } from '../service/product';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './agreements-page.html',
  imports: [
    CommonModule,
    DecimalPipe,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatPaginatorModule,
    MatSortModule,
    MatTableModule,
    MatToolbarModule,
    MatTooltipModule,
    MatSelectModule,
    DatePipe,
    PoSelect,
    RouterModule,
    MatInputModule,
  ],
})
export class AgreementsPage {
  agreementService = inject(AgreementService);
  clientService = inject(ClientService);
  productService = inject(ProductService);
  jira = inject(Jira);

  dialog = inject(MatDialog);

  canEdit = inject(Auth).isAdmin();

  clientCode = input('');
  clientName = computed(() => this.clientService.names()[this.clientCode()]);

  jobNumber = input('');
  pending = signal(false);

  jobTitle = computed(() => this.productService.products().find((it) => it.JobNumber === this.jobNumber())?.Title);

  columnsToShow = signal([
    'ClientCode',
    '_ClientName',
    'JobNumber',
    'AgreementNumber',
    'Title',
    'ServiceType',
    'StartDate',
    'EndDate',
    'BillingCycle',
    'InvoiceAmount',
    'Status',
    'JiraTitle',
    'actions',
  ]);

  sort = viewChild.required(MatSort);
  paginator = viewChild.required(MatPaginator);
  filter = signal('');
  filterPo = signal('');
  filterStatus = signal<string[]>([]);
xg
  statusOptions = signal(AgreementStatuses)

  agreements = computed(() => {
    let agreements = this.agreementService.agreements();

    if (this.clientCode()) {
      agreements = agreements.filter((it) => it.ClientCode === this.clientCode());
    }

    if (this.jobNumber()) {
      agreements = agreements.filter((it) => it.JobNumber === this.jobNumber());
    }

    const mapping = this.clientService.names();
    const po = this.filterPo();
    const status = this.filterStatus();

    if (po) {
      agreements = agreements.filter(({ ProductOwner }) => ProductOwner === po);
    }

    if (status && status.length) {
      agreements = agreements.filter(({ Status }) => status.includes(Status));
    }

    agreements.forEach((item) => (item['_ClientName'] = mapping[item.ClientCode]));

    return agreements;
  });

  source = computed(() => {
    const source = new MatTableDataSource(this.agreements());
    source.sort = this.sort();
    source.paginator = this.paginator();
    source.filter = this.filter();
    return source;
  });

  trackBy(idx: number, row: Agreement) {
    return row.AgreementNumber;
  }

  edit(agreement?: Agreement) {
    const data: AgreementFormData = {
      agreement: agreement,
    };
    if (agreement) {
      data.title = 'Edit agreement';
    }

    this.dialog
      .open(AgreementForm, {
        data,
      })
      .afterClosed()
      .subscribe({
        next: (result) => {
          if (result) {
            this.reload();

            if (agreement) {
              const data: ReservationData = { id: agreement.AgreementNumber };
              this.dialog.open(ReservationDialog, { data });
            }
          }
        },
      });
  }

  reload() {
    this.agreementService.reload();
  }
}
